import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMarketplaceEntities } from './marketplaceData.duck';
import { storableError } from '../util/errors';
import { createImageVariantConfig } from '../util/sdkLoader';
import { matchListingByKeywords } from '../util/search';

// ================ Helper Functions ================ //

const resultIds = (data, keywords) => {
  const listings = data?.data || [];
  return listings
    .filter(l => matchListingByKeywords(l, keywords))
    .map(l => l.id);
};

// ================ Async Thunks ================ //

export const searchModalListings = createAsyncThunk(
  'searchModal/searchModalListings',
  async ({ keywords, config }, thunkAPI) => {
    const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;

    // Use listing image aspect ratio config if available, defaults to 1:1 square
    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = 'listing-card',
    } = config.layout?.listingImage || {};
    const aspectRatio = aspectHeight / aspectWidth;

    const params = {
      perPage: 100,
      include: ['images', 'author'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData',
        'description',
      ],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
      pub_listingType: ['create_tour_packages', 'create_half_day_tour'],
    };

    try {
      const response = await sdk.listings.query(params);
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      // Normalize entities and save to global marketplaceData store
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// ================ Slice ================ //

const searchModalSlice = createSlice({
  name: 'searchModal',
  initialState: {
    resultIds: [],
    totalResults: 0,
    searchInProgress: false,
    searchError: null,
  },
  reducers: {
    clearSearchModal: state => {
      state.resultIds = [];
      state.totalResults = 0;
      state.searchInProgress = false;
      state.searchError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(searchModalListings.pending, (state, action) => {
        state.searchInProgress = true;
        state.searchError = null;
      })
      .addCase(searchModalListings.fulfilled, (state, action) => {
        const matchingIds = resultIds(action.payload.data, action.meta.arg.keywords);
        state.resultIds = matchingIds;
        state.totalResults = matchingIds.length;
        state.searchInProgress = false;
      })
      .addCase(searchModalListings.rejected, (state, action) => {
        state.searchInProgress = false;
        state.searchError = action.payload;
      });
  },
});

export const { clearSearchModal } = searchModalSlice.actions;

export default searchModalSlice.reducer;

